import { z } from 'zod';
import type { Channel } from '.';

/**
 * Schema for workflow item in simulation results
 */
const WorkflowItemSchema = z.object({
  current: z.unknown(),
  lineage: z.array(
    z.object({ type: z.string(), id: z.union([z.number(), z.string()]), nodeName: z.string().optional() })
  ),
  index: z.number(),
});

/**
 * Schema for a single node's execution result
 */
const NodeExecutionResultSchema = z.object({
  nodeId: z.string(),
  status: z.enum(['success', 'error', 'pending']),
  output: z.array(WorkflowItemSchema).optional(),
  inputCount: z.number().optional(),
  outputCount: z.number().optional(),
  duration: z.number().optional(),
  error: z.string().optional(),
  conditionalOutputs: z.record(z.string(), z.array(WorkflowItemSchema)).optional(),
});

/**
 * Schema for simulation error
 */
const SimulationErrorSchema = z.object({
  nodeId: z.string().optional(),
  message: z.string(),
  type: z.enum(['validation', 'execution', 'connection']),
});

/**
 * Channel schemas for recipe simulation realtime updates
 */
export const RecipeSimulationChannelSchema = {
  /**
   * Sent when a node starts executing
   */
  nodeStarted: z.object({ nodeId: z.string(), nodeName: z.string().optional() }),

  /**
   * Sent when a node completes execution
   */
  nodeCompleted: NodeExecutionResultSchema,

  /**
   * Sent when the entire simulation completes
   */
  simulationCompleted: z.object({
    status: z.enum(['success', 'error']),
    nodeResults: z.record(z.string(), NodeExecutionResultSchema),
    totalDuration: z.number(),
    errors: z.array(SimulationErrorSchema),
  }),

  /**
   * Sent when simulation encounters an error
   */
  simulationError: z.object({ message: z.string(), nodeId: z.string().optional() }),
} as const;

/**
 * Recipe simulation channel parameters
 */
export interface RecipeSimulationChannelParams {
  simulationId: string;
  organizationId: number;
}

/**
 * Channel definition for recipe simulation realtime updates.
 * Each simulation gets its own channel identified by simulationId.
 */
export const RecipeSimulationChannel: Channel<RecipeSimulationChannelParams, typeof RecipeSimulationChannelSchema> = {
  getChannelId: ({ simulationId, organizationId }) => `recipeSimulation:${organizationId}:${simulationId}`,
  topics: {
    nodeStarted: 'nodeStarted',
    nodeCompleted: 'nodeCompleted',
    simulationCompleted: 'simulationCompleted',
    simulationError: 'simulationError',
  },
  schemas: RecipeSimulationChannelSchema,
};
